import React from "react";
import { format } from "date-fns";
import { FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { MonthPicker } from "@/components/monthPicker";
import type { RowState } from "@/components/advanceSearch/types";

interface Props {
  row: RowState;
  control: any;
  onClear: (which: "value" | "value2") => void;
}

const placeholder = "MM/YYYY";

export const ConditionMonthInput: React.FC<Props> = ({ row, control, onClear }) => {
  const isBetween = row.operator === "between";

  const buildAriaLabel = (isEnd?: boolean) => {
    if (isEnd) return "Select end month";
    if (isBetween) return "Select start month";
    return "Select month";
  };

  const buildClearLabel = (isEnd?: boolean) => {
    if (isEnd) return "Clear end month";
    if (isBetween) return "Clear start month";
    return "Clear month";
  };

  const renderPicker = (fieldName: string, which: "value" | "value2", options?: { isEnd?: boolean }) => (
    <FormField
      control={control}
      name={fieldName}
      rules={{ required: "This field is required." }}
      render={({ field, fieldState }) => {
        const value = field.value || undefined;

        const handleValueChange = (next?: string) => {
          field.onChange(next ?? undefined);
          if (!next) {
            onClear(which);
          }
        };

        return (
          <FormItem className="relative w-full min-w-0">
            <FormControl>
              <MonthPicker
                {...field}
                value={value}
                onValueChange={handleValueChange}
                placeholder={placeholder}
                ariaLabel={buildAriaLabel(options?.isEnd)}
                clearAriaLabel={buildClearLabel(options?.isEnd)}
                displayFormatter={(month) => format(month, "MM/yyyy")}
                invalid={Boolean(fieldState.error)}
                wrapperClassName="min-w-0"
              />
            </FormControl>
            <FormMessage className={"absolute left-0 top-full mt-1 text-xs text-red-600"} />
          </FormItem>
        );
      }}
    />
  );

  if (isBetween) {
    return (
      <div className="grid items-center gap-3 md:grid-cols-[1fr_auto_1fr] max-w-full min-w-0">
        <div className="min-w-0">{renderPicker(`value_${row.id}`, "value")}</div>

        <span className="text-center font-bold text-inherit whitespace-nowrap">To</span>

        <div className="min-w-0">{renderPicker(`value2_${row.id}`, "value2", { isEnd: true })}</div>
      </div>
    );
  }

  return renderPicker(`value_${row.id}`, "value");
};
